<script lang="ts">
  import { storyblokEditable } from "@storyblok/svelte";
  import { onMount } from "svelte";
  import { page } from "$app/stores";
  import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
  import { deviceDetails } from "$lib/consumer/stores/deviceDetails";
  import GetDeliveryButton from "./GetDeliveryButton.svelte";
  import gsap from "gsap";

  // Get the block data
  export let blok;

  // Set the debug flags
  $: debugAnimation = $page.url.searchParams.get("debug") === "1";
  $: hideAnimation = $page.url.searchParams.get("hide") === "1";

  // Get the device details
  $: ({ isMobile, isTablet, isDesktop, isAndroid, isIOS } = $deviceDetails);

  // References for the animation elements
  let droneCanvas: HTMLCanvasElement;
  let phoneImage: HTMLImageElement;
  let phoneImageRect: DOMRect;
  let phoneImagePixelsFromLeft: number;
  let phoneImagePercentFromLeft: number;
  let dronePhoneYOffset = 0;
  let frame1: HTMLElement;
  let frame1Content: HTMLElement;
  let frame1Clouds: HTMLElement;
  let frame2: HTMLElement;
  let frame2Content: HTMLElement;
  let frame2Clouds: HTMLElement;
  let frame3: HTMLElement;
  let frame3Content: HTMLElement;
  let frame3Clouds: HTMLElement;
  let frame4: HTMLElement;
  let frame4Content: HTMLElement;
  let frame4Heading: HTMLElement;

  /**
   * Starts the background clouds animation
   */
  function startBackgroundAnimation() {
    gsap.to(".clouds-background", {
      scrollTrigger: {
        trigger: ".clouds-background",
        scrub: 2,
      },
      y: "-500",
    });
  }

  /**
   * Starts the frame pinning animation sequence
   */
  function startFrameAnimationSequence() {
    // Initializes a new gsap timeline
    const tl = gsap.timeline();

    // Set the initial opacity of the foreground clouds
    tl.set(frame1Clouds, { opacity: 0.5 });
    tl.set(frame2Clouds, { opacity: 0.5 });
    tl.set(frame3Clouds, { opacity: 0.5 });

    /**
     * Frame 1
     */

    // Pin frame
    tl.to(frame1, {
      scrollTrigger: {
        trigger: frame1,
        start: "top 25%",
        pin: true,
        anticipatePin: isMobile ? 1 : 0.5,
      },
    });

    // Slide frame content up and fade it out
    tl.to(frame1Content, {
      scrollTrigger: {
        trigger: frame1,
        start: "top 30%",
        scrub: 0.5,
      },
      y: -200,
    });

    // Fade phone image out
    tl.to(phoneImage, {
      scrollTrigger: {
        trigger: frame1,
        start: "top 30%",
        scrub: 0.5,
      },
      opacity: 0,
      scale: 0.5,
    });

    // Slide foreground clouds across the viewport from right to left
    tl.to(frame1Clouds, {
      scrollTrigger: {
        trigger: frame1,
        start: "top 40%",
        scrub: 2,
      },
      y: isMobile ? "5vh" : "40vh",
      x: "-200vw",
      opacity: 0.4,
    });

    /**
     * Frame 2
     */

    // Pin frame
    tl.to(frame2, {
      scrollTrigger: {
        trigger: frame2,
        start: "top 35%",
        pin: true,
        anticipatePin: isMobile ? 1 : 0.5,
      },
    });

    // Slide frame content up and fade it out
    tl.to(frame2Content, {
      scrollTrigger: {
        trigger: frame2,
        start: "top 35%",
        scrub: 0.5,
      },
      y: -200,
    });

    // Slide foreground clouds across the viewport from right to left
    tl.to(frame2Clouds, {
      scrollTrigger: {
        trigger: frame2,
        start: "top center",
        scrub: 2,
      },
      x: "-150vw",
    });

    /**
     * Frame 3
     */

    // Pin frame
    tl.to(frame3, {
      scrollTrigger: {
        trigger: frame3,
        start: "top 35%",
        pin: true,
        anticipatePin: isMobile ? 1 : 0.5,
      },
    });

    // Slide frame content up and fade it out
    tl.to(frame3Content, {
      scrollTrigger: {
        trigger: frame3,
        start: "top 35%",
        scrub: 0.5,
      },
      y: -150,
    });

    // Slide foreground clouds across the viewport from right to left
    tl.to(frame3Clouds, {
      scrollTrigger: {
        trigger: frame3,
        start: "top center",
        scrub: 2,
      },
      x: "-100vw",
    });

    /**
     * Frame 4
     */

    // Pin frame to the bottom of the viewport
    tl.to(frame4, {
      scrollTrigger: {
        trigger: frame4,
        start: "bottom bottom",
        end: () => (frame4 ? `+=${frame4.offsetHeight * 0.8}` : "+=70%"),
        pin: true,
        anticipatePin: isMobile ? 1 : 0.5,
      },
    });

    // Lock frame content to the bottom of the viewport and slide up slightly
    tl.to(frame4Content, {
      scrollTrigger: {
        trigger: frame4,
        scrub: 2,
      },
      transformOrigin: "center bottom",
      y: -75,
      ease: "power2.out",
    });
  }

  /**
   * Starts the drone animation sequence
   */
  function startDroneAnimationSequence() {
    const context = droneCanvas?.getContext("2d");

    // Define the animation sequence groups
    const sequenceGroups = [
      {
        name: "zipAscending",
        start: 0,
        end: 49,
        trigger: ".frame1",
        endTrigger: ".frame1",
        scrollStart: "top center",
        scrub: true,
      },
      {
        name: "zipBanking",
        start: 50,
        end: 99,
        trigger: ".frame2",
        endTrigger: ".frame2",
        scrollStart: "top center",
        scrub: true,
      },
      {
        name: "droidDescending",
        start: 100,
        end: 141,
        trigger: ".frame3",
        endTrigger: ".frame3",
        scrollStart: "top center",
        scrub: true,
      },
      {
        name: "droidDelivering",
        start: 142,
        end: 254,
        trigger: ".frame4",
        endTrigger: ".frame4",
        scrollStart: "top center",
        scrub: true,
      },
    ];

    // Set the initial drone canvas size (mobile : desktop)
    droneCanvas.width = isMobile ? 540 : 810;
    droneCanvas.height = isMobile ? 960 : 1440;

    // Loop through each sequence group
    sequenceGroups.forEach((group) => {
      const images: HTMLImageElement[] = [];
      const sequence = { frame: 0 };

      // Create the initial timeline and scroll trigger for each sequence
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: group.trigger,
          endTrigger: group.endTrigger,
          start: group.scrollStart,
          scrub: group.scrub,
        },
        ease: "none",
      });

      // Zip Ascending sequence

      if (group.name === "zipAscending") {
        tl.fromTo(
          droneCanvas,
          {
            height: isMobile || isTablet ? "230px" : "400px",
            position: "absolute",
            top: () => `${dronePhoneYOffset}`,
            left: () => `${phoneImagePercentFromLeft}%`,
            xPercent: -50,
            rotate: 10,
            opacity: 1,
            zIndex: 3,
          },
          {
            id: "zipAscending",
            scrollTrigger: {
              trigger: ".frame1",
              start: isMobile ? "top 30%" : "top 30%",
              scrub: 0.5,
            },
            left: "50%",
            height: isMobile ? "90vh" : "100vh",
            rotate: 0,
          },
        );

        tl.to(droneCanvas, {
          scrollTrigger: {
            trigger: droneCanvas,
            start: `top ${dronePhoneYOffset}`,
            scrub: true,
          },
          position: "fixed",
        });
      }

      // Zip Banking sequence

      if (group.name === "zipBanking") {
        tl.to(droneCanvas, {
          scrollTrigger: {
            trigger: ".frame2",
            scrub: 0.5,
          },
          top: 0,
        });
      }

      if (group.name === "droidDescending") {
        tl.to(droneCanvas, {
          scrollTrigger: {
            trigger: ".frame3",
            start: "top bottom",
            scrub: 0.5,
          },
          xPercent: isMobile ? -56 : -56, // Slide the canvas left to horizontally align package delivery
          yPercent: isMobile ? (isAndroid ? -10 : -5) : -10, // Slide the canvas up to vertically align package delivery
        });
      }

      // Droid Delivering sequence

      if (group.name === "droidDelivering") {
        // Move the canvas upwards as the viewer scrolls past the final frame
        tl.to(droneCanvas, {
          scrollTrigger: {
            trigger: ".how-it-works-section",
            start: "bottom bottom",
            end: "bottom top",
            scrub: true,
          },
          y: "-100vh",
          ease: "none",
        });
      }

      // Preload the appropriate drone images (mobile or desktop)
      let deviceTypePath = isTablet || isDesktop ? "desktop" : "mobile";

      for (let i = group.start; i <= group.end; i++) {
        const image = new Image();
        image.src = `/images/home/howitworks/drone-sequence/${deviceTypePath}/${(i + 1)
          .toString()
          .padStart(4, "0")}.webp`;
        images.push(image);
      }

      // Animate the drone image sequence
      tl.to(sequence, {
        frame: images.length - 1,
        snap: "frame",
        ease: "none",
        onUpdate: () => {
          // If the current frame is loaded, clear the frame and draw a new one
          if (images[sequence.frame]?.complete) {
            context?.clearRect(0, 0, droneCanvas.width, droneCanvas.height);
            context?.drawImage(images[sequence.frame], 0, 0);
          }
        },
      });

      // Load the first image in the first sequence
      images[0].onload = () => {
        if (group.start === 0) {
          if (context) {
            context.drawImage(images[0], 0, 0);
          }
        }
      };
    });
  }

  // Get the y offset for the drone canvas element in relation to the phone image
  function getDroneCanvasStartingYPosition() {
    if (isMobile) {
      dronePhoneYOffset = 310;
    } else if (isTablet) {
      dronePhoneYOffset = 120;
    } else if (isDesktop) {
      dronePhoneYOffset = 150;
    }
  }

  // Get the percentage distance of the phone image element to the left of the viewport
  function getPhoneImagePercentFromLeft() {
    if (phoneImage) {
      phoneImageRect = phoneImage.getBoundingClientRect();
      phoneImagePixelsFromLeft = phoneImageRect.left + phoneImageRect.width / 2;
      phoneImagePercentFromLeft = (phoneImagePixelsFromLeft / window.innerWidth) * 100;
    }
  }

  onMount(() => {
    // Initialize animations
    const gsapContext = gsap.context(() => {
      startBackgroundAnimation();
      startFrameAnimationSequence();
      getDroneCanvasStartingYPosition();
      getPhoneImagePercentFromLeft();
      startDroneAnimationSequence();
    });

    // Reinitialize animations on window resize
    function handleWindowResize() {
      // Store current scroll position
      const scrollPos = window.scrollY;

      // Cleanup animations
      gsapContext.revert();

      // Re-initialize animations
      gsapContext.add(() => {
        getDroneCanvasStartingYPosition();
        getPhoneImagePercentFromLeft();
        startBackgroundAnimation();
        startFrameAnimationSequence();
        startDroneAnimationSequence();
      });

      // Recalculate scroll trigger positions
      ScrollTrigger.refresh(true);

      // Reset window scroll position
      window.scrollTo(0, scrollPos);
    }

    if (isDesktop) {
      window.addEventListener("resize", handleWindowResize);
    }

    // Unmount
    return () => {
      // Cleanup event listeners and animations
      window.removeEventListener("resize", handleWindowResize);
      gsapContext.revert();
    };
  });
</script>

<section
  class="how-it-works-section"
  class:isAndroid
  class:debugAnimation
  class:hideAnimation
  use:storyblokEditable={blok}
>
  <div class="section-header-width mx-auto">
    <div class="foreground-clouds foreground-clouds-1" bind:this={frame1Clouds}></div>

    <div class="frame frame1" bind:this={frame1}>
      <div class="frame-content frame1-content" bind:this={frame1Content}>
        <h1 class="frame1-heading">{@html blok.firstHeading}</h1>
        <img
          class="phone-image"
          src="/images/home/howitworks/phone.webp"
          alt="Zipline app being used to make an order"
          bind:this={phoneImage}
        />
      </div>
    </div>

    <div class="frame frame2" bind:this={frame2}>
      <div class="foreground-clouds foreground-clouds-2" bind:this={frame2Clouds}></div>
      <div class="frame-content frame2-content" bind:this={frame2Content}>
        <h2 class="frame2-heading">{@html blok.secondHeading}</h2>
      </div>
    </div>

    <div class="frame frame3" bind:this={frame3}>
      <div class="foreground-clouds foreground-clouds-3" bind:this={frame3Clouds}></div>
      <div class="frame-content frame3-content" bind:this={frame3Content}>
        <h2 class="frame3-heading">{@html blok.thirdHeading}</h2>
      </div>
    </div>
  </div>

  <div class="frame frame4" bind:this={frame4}>
    <div class="frame-content frame4-content" bind:this={frame4Content}>
      <h3 class="frame4-heading" bind:this={frame4Heading}>{@html blok.fourthHeading}</h3>
      <GetDeliveryButton {blok} />
    </div>
  </div>

  <canvas id="drone-canvas" bind:this={droneCanvas} />
</section>

<style lang="postcss">
  /**
   * Debug
   */

  .debugAnimation .frame {
    border: 2px solid blue;
  }

  .debugAnimation #drone-canvas {
    border: 2px solid red;
  }

  .hideAnimation.how-it-works-section {
    display: none;
  }

  /**
   * Section
   */

  .how-it-works-section {
    position: relative;
    margin-top: 8rem;
    overflow: hidden;
  }

  /**
   * Drone Canvas
   */

  #drone-canvas {
    position: fixed;
    pointer-events: none;
    opacity: 0;
    z-index: 100;
  }

  /**
   * Foreground Clouds
   */

  .foreground-clouds {
    position: absolute;
    top: -50vw;
    width: 300vw;
    height: 110vh;
    background-image: url("/images/home/howitworks/foreground-clouds.png");
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
    z-index: 200;

    @screen lg {
      top: -100vh;
      height: 300vh;
    }
  }

  .foreground-clouds-1 {
    top: 40vh;

    @screen md {
      top: 0;
    }

    @screen lg {
      top: -20vh;
    }
  }

  /**
   * Frame Sizing
   */

  .frame {
    position: relative;
  }

  .frame1 {
    height: 50vh;

    @screen md {
      height: 65vh;
    }
  }

  .frame2 {
    height: 50vh;
  }

  .frame3 {
    height: 50vh;
  }

  .frame4 {
    position: relative;
    height: 125vh;
  }

  /**
   * Frame Content
   */

  .frame1-content {
    display: grid;
    grid-template-rows: 1fr auto;
    gap: 2rem;

    @screen md {
      grid-template-columns: 1fr 1fr;
      align-items: center;
    }
  }

  .frame1-content .phone-image {
    margin: 0 auto;
    border-radius: 24px;

    @screen xl {
      max-width: 550px;
    }
  }

  .frame4 {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    text-align: center;
    background-image: url("/images/home/howitworks/backyard-mobile.webp");
    background-repeat: no-repeat;
    background-position: center top;
    background-size: cover;

    @screen md {
      background-image: url("/images/home/howitworks/backyard-desktop.webp");
    }
  }

  .frame4-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: -40px;

    @screen md {
      margin-bottom: -15px;
    }
  }

  /* Make sure the CTA button isn't cropped at the bottom on Android devices */
  .isAndroid .frame4-content {
    margin-bottom: 10px;
  }

  /**
   * Frame Headings
   */

  .frame1-heading,
  .frame2-heading,
  .frame3-heading {
    font-size: 70px;
    line-height: 1;
    font-weight: var(--font-weight-semibold);
    font-family: var(--font-secondary);
    white-space: pre-wrap;
    color: rgb(var(--color-blue-primary));

    @media (min-width: 768px) {
      font-size: 90px;
    }

    @media (min-width: 1024px) {
      font-size: 120px;
    }

    @media (min-width: 1280px) and (max-height: 900px) {
      font-size: 150px;
    }

    @media (min-width: 1280px) and (min-height: 1024px) {
      font-size: 190px;
    }
  }

  .frame4-heading {
    margin-bottom: 1.5rem;
    font-size: 60px;
    line-height: 1.1;
    font-weight: var(--font-weight-semibold);
    font-family: var(--font-secondary);
    color: #fff;
    text-shadow: 0px 1px 10px rgba(0, 0, 0, 0.2);
    white-space: pre-wrap;

    @media (min-width: 768px) and (min-height: 900px) {
      font-size: 80px;
    }

    @media (min-width: 1280px) and (min-height: 1024px) {
      font-size: 100px;
    }

    /* For wide viewports taller than 2100px */
    @media (min-width: 2100px) and (min-height: 1200px) {
      font-size: 120px;
    }
  }
</style>
